import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign } from 'lucide-react';
import { MetricTile } from '@/components/shared/MetricTile';
import { useIsMobile } from '@/hooks/use-mobile';
import { plotlyChartChrome, usePlotlyTheme } from '@/lib/plotlyTheme';

interface EconomicIndicator {
  year: number;
  area_code: string;
  unemployment_rate?: number;
  gdp_growth?: number;
  recession_flag?: number;
  recovery_phase?: string;
  industry_performance_index?: number;
}

interface WageHistory {
  year: number;
  a_median: number;
  employment: number;
}

interface EconomicContextProps {
  indicators: EconomicIndicator[];
  wageHistory: WageHistory[];
  occupationTitle?: string;
  areaName?: string;
}

export const EconomicContext: React.FC<EconomicContextProps> = ({
  indicators,
  wageHistory,
  occupationTitle = "Selected Occupation",
  areaName = "Selected Area"
}) => {
  const isMobile = useIsMobile();
  const { colors, themeKey } = usePlotlyTheme();

  const analysisData = useMemo(() => {
    if (!indicators || !wageHistory || indicators.length === 0 || wageHistory.length === 0) {
      return null;
    }

    const alignedData = wageHistory.map(wage => {
      const indicator = indicators.find(ind => ind.year === wage.year);
      return {
        year: wage.year,
        salary: wage.a_median,
        employment: wage.employment,
        unemployment_rate: indicator?.unemployment_rate || 0,
        gdp_growth: indicator?.gdp_growth || 0,
        recession_flag: indicator?.recession_flag || 0,
        recovery_phase: indicator?.recovery_phase || '',
        industry_performance: indicator?.industry_performance_index || 0
      };
    }).filter(d => d.unemployment_rate > 0 || d.gdp_growth !== 0);

    return alignedData;
  }, [indicators, wageHistory]);

  if (!analysisData || analysisData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Economic Context Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No economic context data available for this selection
          </div>
        </CardContent>
      </Card>
    );
  }

  const years = analysisData.map(d => d.year);
  const recessionYears = analysisData.filter(d => d.recession_flag === 1).map(d => d.year);

  // Create traces for dual-axis chart
  const traces = [
    // Salary trend (primary y-axis)
    {
      x: years,
      y: analysisData.map(d => d.salary),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Median Salary',
      line: { color: '#6366f1', width: 3 },
      marker: { color: '#6366f1', size: 6 },
      yaxis: 'y'
    },
    // Unemployment rate (secondary y-axis)
    {
      x: years,
      y: analysisData.map(d => d.unemployment_rate),
      type: 'scatter',
      mode: 'lines',
      name: 'Unemployment Rate',
      line: { color: '#ef4444', width: 2 },
      yaxis: 'y2'
    },
    // GDP growth (secondary y-axis)
    {
      x: years,
      y: analysisData.map(d => d.gdp_growth),
      type: 'scatter',
      mode: 'lines',
      name: 'GDP Growth',
      line: { color: '#22c55e', width: 2 },
      yaxis: 'y2'
    }
  ];

  // Add recession shading as area fills
  recessionYears.forEach(year => {
    traces.push({
      x: [year - 0.5, year + 0.5, year + 0.5, year - 0.5],
      y: [0, 0, 1, 1],
      type: 'scatter',
      mode: 'lines',
      fill: 'toself',
      line: { color: colors.recessionBorder, width: 1 },
      fillcolor: colors.recessionFill,
      showlegend: false,
      hoverinfo: 'skip',
      yaxis: 'y3',
      name: 'Recession'
    } as unknown);
  });

  const chrome = plotlyChartChrome(colors, { isMobile });

  const layout = {
    title: {
      text: `${occupationTitle} Economic Context - ${areaName}`,
      font: chrome.titleFont
    },
    xaxis: {
      title: chrome.axisTitle('Year'),
      type: 'linear',
      ...chrome.axisDefaults
    },
    yaxis: {
      title: chrome.axisTitle('Median Annual Salary ($)'),
      tickformat: '$,.0f',
      side: 'left',
      ...chrome.axisDefaults
    },
    yaxis2: {
      title: chrome.axisTitle('Economic Indicators (%)'),
      tickformat: '.1f',
      side: 'right',
      overlaying: 'y',
      range: [-5, 15],
      ...chrome.axisDefaults
    },
    yaxis3: {
      overlaying: 'y',
      range: [0, 1],
      showgrid: false,
      showticklabels: false,
      zeroline: false
    },
    hovermode: 'x unified',
    hoverlabel: chrome.hoverlabel,
    showlegend: true,
    legend: chrome.legend('overlay'),
    margin: { l: 80, r: 96, t: 60, b: 60 },
    paper_bgcolor: chrome.paper_bgcolor,
    plot_bgcolor: chrome.plot_bgcolor,
    font: chrome.font
  };

  const config = {
    displayModeBar: !isMobile,
    modeBarButtonsToAdd: ['pan2d', 'autoScale2d'],
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false,
    responsive: true
  };

  // Calculate correlations
  const salaryValues = analysisData.map(d => d.salary);
  const unemploymentValues = analysisData.map(d => d.unemployment_rate);
  const gdpValues = analysisData.map(d => d.gdp_growth);

  const calculateCorrelation = (x: number[], y: number[]) => {
    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((acc, xi, i) => acc + xi * y[i], 0);
    const sumXX = x.reduce((acc, xi) => acc + xi * xi, 0);
    const sumYY = y.reduce((acc, yi) => acc + yi * yi, 0);
    
    const correlation = (n * sumXY - sumX * sumY) / 
      Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));
    
    return isNaN(correlation) ? 0 : correlation;
  };

  const unemploymentCorr = calculateCorrelation(salaryValues, unemploymentValues);
  const gdpCorr = calculateCorrelation(salaryValues, gdpValues);

  // Latest economic indicators
  const latestIndicator = analysisData[analysisData.length - 1];
  const isCurrentRecession = latestIndicator.recession_flag === 1;
  const currentPhase = latestIndicator.recovery_phase || 'Stable';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Economic Context Analysis</CardTitle>
        <CardDescription>
          Salary trends correlated with economic indicators over {analysisData.length} years
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Economic Status */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricTile
            tone={isCurrentRecession ? 'red' : 'green'}
            icon={isCurrentRecession ? AlertTriangle : TrendingUp}
            badge={isCurrentRecession ? 'Risk' : 'Stable'}
            value={currentPhase}
            label="Economic Phase"
            compactValue
          />
          <MetricTile
            tone="orange"
            icon={TrendingDown}
            badge="Rate"
            value={`${latestIndicator.unemployment_rate.toFixed(1)}%`}
            label="Unemployment"
          />
          <MetricTile
            tone={latestIndicator.gdp_growth >= 0 ? 'green' : 'red'}
            icon={TrendingUp}
            badge="GDP"
            value={`${latestIndicator.gdp_growth.toFixed(1)}%`}
            label="GDP Growth"
          />
          <MetricTile
            tone="purple"
            icon={DollarSign}
            badge="Latest"
            value={`$${latestIndicator.salary.toLocaleString()}`}
            label="Current Salary"
          />
        </div>

        {/* Interactive Chart */}
        <div className="h-80 min-w-0 w-full overflow-x-auto">
          <Plot
            data={traces}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            key={`${themeKey}-${isMobile ? 'mobile' : 'desktop'}`}
          />
        </div>

        {/* Correlation Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium">Economic Correlations</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Salary vs Unemployment</span>
                <Badge variant={unemploymentCorr < -0.3 ? "destructive" : unemploymentCorr > 0.3 ? "default" : "secondary"}>
                  {unemploymentCorr.toFixed(2)}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>Salary vs GDP Growth</span>
                <Badge variant={gdpCorr > 0.3 ? "default" : gdpCorr < -0.3 ? "destructive" : "secondary"}>
                  {gdpCorr.toFixed(2)}
                </Badge>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">Recession Impact</h4>
            <div className="text-sm text-muted-foreground">
              {recessionYears.length > 0 ? (
                <>
                  {recessionYears.length} recession periods identified:
                  <div className="mt-1">
                    {recessionYears.join(", ")}
                  </div>
                </>
              ) : (
                "No recession periods in available data"
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};